import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import '../src/i18n';

// Pages
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import BadgeKioskPage from './pages/BadgeKioskPage';
import TabletTimeClockKioskPage from './pages/TabletTimeClockKioskPage';
import PrivacyPage from './pages/PrivacyPage';
import AccessibilityPage from './pages/AccessibilityPage';
import DashboardPage from './pages/DashboardPage';
import StaffPage from './pages/StaffPage';
import SchedulePage from './pages/SchedulePage';
import MonthlyProgramPage from './pages/MonthlyProgramPage';
import LeavesPage from './pages/LeavesPage';
import TimeClockPage from './pages/TimeClockPage';
import BadgesPage from './pages/BadgesPage';
import PatientsPage from './pages/PatientsPage';
import CentersPage from './pages/CentersPage';
import ReportsPage from './pages/ReportsPage';
import TransportPage from './pages/TransportPage';
import IncidentsPage from './pages/IncidentsPage';
import ActivitiesPage from './pages/ActivitiesPage';
import ActivityCatalogPage from './pages/ActivityCatalogPage';
import PermissionsPage from './pages/PermissionsPage';
import PrivacyIncidentsPage from './pages/PrivacyIncidentsPage';
import ProfilePage from './pages/ProfilePage';
import Layout from './components/common/Layout';
import DocumentLanguageSync from './components/common/DocumentLanguageSync';

// Protected route wrapper
const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.mustChangePassword) return <Navigate to="/change-password" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

const AppRoutes = () => {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={!user ? <LoginPage /> : <Navigate to="/" replace />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/change-password" element={<ChangePasswordPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/accessibility" element={<AccessibilityPage />} />
      <Route path="/badge-scan" element={<BadgeKioskPage />} />
      <Route path="/badge-kiosk" element={<Navigate to="/badge-scan" replace />} />
      <Route path="/timeclock-kiosk" element={<TabletTimeClockKioskPage />} />

      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<DashboardPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="staff" element={<ProtectedRoute roles={['admin', 'manager']}><StaffPage /></ProtectedRoute>} />
        <Route path="schedule" element={<ProtectedRoute roles={['admin', 'manager', 'staff']}><SchedulePage /></ProtectedRoute>} />
        <Route path="monthly-program" element={<ProtectedRoute roles={['admin', 'manager', 'staff', 'usager']}><MonthlyProgramPage /></ProtectedRoute>} />
        <Route path="leaves" element={<ProtectedRoute roles={['admin', 'manager', 'staff']}><LeavesPage /></ProtectedRoute>} />
        <Route path="timeclock" element={<Navigate to="/" replace />} />
        <Route path="badges" element={<ProtectedRoute roles={['admin', 'manager']}><BadgesPage /></ProtectedRoute>} />
        <Route path="patients" element={<ProtectedRoute roles={['admin', 'manager', 'staff', 'usager']}><PatientsPage /></ProtectedRoute>} />
        <Route path="activities" element={<ProtectedRoute roles={['admin', 'manager', 'staff', 'usager']}><ActivitiesPage /></ProtectedRoute>} />
        <Route path="activity-catalog" element={<ProtectedRoute roles={['admin']}><ActivityCatalogPage /></ProtectedRoute>} />
        <Route path="transport" element={<ProtectedRoute roles={['admin', 'manager', 'staff', 'usager']}><TransportPage /></ProtectedRoute>} />
        <Route path="incidents" element={<ProtectedRoute roles={['admin', 'manager', 'staff']}><IncidentsPage /></ProtectedRoute>} />
        <Route path="centers" element={<ProtectedRoute roles={['admin']}><CentersPage /></ProtectedRoute>} />
        <Route path="permissions" element={<ProtectedRoute roles={['admin']}><PermissionsPage /></ProtectedRoute>} />
        <Route path="privacy-incidents" element={<ProtectedRoute roles={['admin']}><PrivacyIncidentsPage /></ProtectedRoute>} />
        <Route path="reports" element={<ProtectedRoute roles={['admin', 'manager', 'staff', 'usager']}><ReportsPage /></ProtectedRoute>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App = () => (
  <BrowserRouter>
    <AuthProvider>
      <DocumentLanguageSync />
      <AppRoutes />
    </AuthProvider>
  </BrowserRouter>
);

export default App;
